import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Ban, Check } from 'lucide-react';
import { useState } from 'react';

import {
  contentFieldsChangeType,
  contentFieldsTypeChangePreview,
} from '@/shared/api/generated/content/content';
import type { FieldBody, FieldTypeBody, TypeChangeOutputBody } from '@/shared/api/generated/model';
import { ApiError } from '@/shared/api/http';
import { Button } from '@/shared/ui/Button';
import { Dialog, DialogContent } from '@/shared/ui/Dialog';
import { Field } from '@/shared/ui/Field';
import { Select, SelectItem } from '@/shared/ui/Select';
import { toast } from '@/shared/ui/Toast';

import styles from './SchemaPage.module.scss';

export interface FieldTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  field: FieldBody;
  fieldTypes: FieldTypeBody[];
  onDone: () => void;
}

/**
 * Смена вида поля.
 *
 * Весь смысл окна в том, что последствия видны до нажатия, а не после.
 * Сервер считает, сколько записей затронуто и у скольких значение не удастся
 * перенести; здесь это только показывается. Считать то же самое на клиенте
 * значило бы завести вторую реализацию преобразования, которая однажды
 * разойдётся с настоящей.
 */
export function FieldTypeDialog({
  open,
  onOpenChange,
  field,
  fieldTypes,
  onDone,
}: FieldTypeDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <Body field={field} fieldTypes={fieldTypes} onOpenChange={onOpenChange} onDone={onDone} />
      )}
    </Dialog>
  );
}

function Body({ field, fieldTypes, onOpenChange, onDone }: Omit<FieldTypeDialogProps, 'open'>) {
  const [target, setTarget] = useState(field.type);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const changed = target !== field.type;

  const preview = useQuery({
    queryKey: ['field-type-change', field.id, target],
    enabled: changed,
    // Предпросмотр обязан отражать сегодняшние записи, а не то, что лежало в
    // кеше пять минут назад: между открытиями окна их могли заполнить.
    staleTime: 0,
    queryFn: async () => {
      const response = await contentFieldsTypeChangePreview(field.id, { to: target });

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос предпросмотра');
      }

      return response.data;
    },
  });

  const apply = () => {
    setError(undefined);
    setBusy(true);

    contentFieldsChangeType(field.id, { to: target }, { 'If-Match': field.etag })
      .then(() => {
        toast.success('Вид поля изменён');
        onDone();
        onOpenChange(false);
      })
      .catch((cause: unknown) => {
        setError(
          cause instanceof ApiError ? cause.detail : 'Не удалось изменить. Попробуйте ещё раз.',
        );
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const impossible = preview.data?.kind === 'impossible';

  return (
    <DialogContent
      title={`Вид поля «${field.name}»`}
      description="Значения во всех записях будут преобразованы. Что именно произойдёт — видно ниже."
      size="sm"
      dismissible={false}
      footer={
        <>
          <Button
            variant="outline"
            tone="neutral"
            type="button"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Отмена
          </Button>
          <Button
            type="button"
            tone={preview.data?.kind === 'lossy' ? 'danger' : 'accent'}
            loading={busy}
            disabled={!changed || impossible || preview.isPending}
            onClick={apply}
          >
            Сменить вид
          </Button>
        </>
      }
    >
      <Field label="Новый вид" error={error}>
        <Select value={target} onValueChange={setTarget}>
          {fieldTypes.map((item) => (
            <SelectItem
              key={item.type}
              value={item.type}
              description={item.type === field.type ? 'сейчас' : item.type}
            >
              {item.title}
            </SelectItem>
          ))}
        </Select>
      </Field>

      {changed && preview.isPending && <p className={styles['preview__wait']}>Считаем…</p>}

      {changed && preview.data !== undefined && <Outcome outcome={preview.data} />}
    </DialogContent>
  );
}

function Outcome({ outcome }: { outcome: TypeChangeOutputBody }) {
  const tone = outcome.kind === 'impossible' ? 'bad' : outcome.kind === 'lossy' ? 'warn' : 'good';

  const Icon =
    outcome.kind === 'impossible' ? Ban : outcome.kind === 'lossy' ? AlertTriangle : Check;

  return (
    <div className={`${styles['preview'] ?? ''} ${styles[`preview--${tone}`] ?? ''}`}>
      <p className={styles['preview__head']}>
        <Icon size={15} />
        {outcome.explain}
      </p>

      {outcome.kind !== 'impossible' && (
        <ul className={styles['preview__list']}>
          <li>
            Записей у этого типа: <b>{outcome.records_count}</b>
          </li>
          <li>
            Поле заполнено у: <b>{outcome.with_value_count}</b>
          </li>
          {outcome.will_clear_count > 0 && (
            <li className={styles['preview__loss']}>
              Значение опустеет у: <b>{outcome.will_clear_count}</b>
            </li>
          )}
        </ul>
      )}

      {outcome.kind === 'lossy' && outcome.will_clear_count === 0 && (
        <p className={styles['preview__note']}>
          Сейчас терять нечего: ни одно из заполненных значений не пострадает.
        </p>
      )}
    </div>
  );
}
